import React, { ReactElement, useEffect, useState } from 'react';
import './Infrastructure.less';
import Photos from './Photos/Photos';
import Facilities, { FacilityProps } from './Facilities/Facilities';
import Panel from '../../../../../shared/components/card/Panel';
import { useDispatch, useSelector } from 'react-redux';
import {
  HospitalProfileProperty,
  RoomDetailsRequest,
  SaveProfilePropertyRequest,
  SaveProfilePropertyResponse,
} from '../../../../../shared/models/hospitalProfile.model';
import {
  APPLICATION_ID,
  authFeatureConstants,
  DOCUMENT_TYPE_INFRA_PHOTOS,
  FACILITY_GROUP_NAME,
  FILE_UPLOAD,
  REQUEST_PAYLOAD,
  SAVE_DOCUMENT,
  SAVE_PROPERTY,
  STATUS_SUBMIT,
} from '../../../../../shared/constants/commonConstants';
import SendRequest from '../../../../../shared/services/sendRequest.service';
import { Button } from 'antd';
import { FileDetail } from '../../../../../shared/models/file.profile.model';
import {
  DocumentDetail,
  SaveDocumentRequest,
  SaveDocumentResponse,
} from '../../../../../shared/models/document.profile.model';
import { UploadFile } from '../../../../../shared/models/uploadFiles.model';
import { canActivate, error, success } from '../../../../../utils/utility';
import { fetchHospitalProfile } from '../../../../../store/actions/hospitalAction';
import Rooms from './Rooms/Rooms';
import _ from 'lodash';
import { RoomDetail } from '../../../../../shared/models/room.model';
import { SAVE_ROOMS } from '../../../../../shared/constants/urlEndPoints';

const Infrastructure = (props: any): ReactElement => {
  const [facilities, setFacilities] = useState([] as FacilityProps[]);
  const [hospDetails, setHospDetails] = useState(null as any);
  const [rooms, setRooms] = useState([] as any);
  const [isSuccess, setIsSuccess] = useState(false);
  const [photos, setPhotos] = useState([] as any);
  const [roomsData, setRoomsData] = useState(null as any);
  const dispatch = useDispatch();
  const apiHospitalProfile = useSelector(
    ({ hospital: { hospitalProfile } }: any): any => hospitalProfile,
  );

  useEffect((): void => {
    if (apiHospitalProfile) {
      setHospDetails(apiHospitalProfile);
    }
  }, [apiHospitalProfile]);

  useEffect((): void => {
    if (hospDetails) {
      getRoomDetails();
    }
  }, [hospDetails]);

  const isNewEntry = useSelector(
    ({ hospital: { newEntry } }: any): any => newEntry,
  );
  const getFacilities = (f: any): void => {
    setFacilities(f);
  };
  const getRooms = (r: any): void => {
    setRooms(r);
  };
  const getPhotos = (p: any): void => {
    const photosToSave = p.map((x: any): any => {
      if (x.isActive) {
        return { ..._.assignIn({}, x.file), file: x.file, isActive: true };
      } else {
        return { ..._.assignIn({}, x.file), isActive: false };

      }
    });
    setPhotos([...photosToSave]);
  };

  const saveFacilities = async (): Promise<any> => {
    if (facilities !== null && facilities.length > 0) {
      const properties: HospitalProfileProperty[] = facilities.map(
        (item: FacilityProps): HospitalProfileProperty => {
          return {
            profileId: hospDetails[0].id,
            isActive: true,
            name: item.facilityName,
            value: item.isApplicable.toString(),
            approvalRemarks: '',
            statusName: STATUS_SUBMIT,
            groupName: FACILITY_GROUP_NAME,
            id: 0,
          };
        },
      );

      const payload: SaveProfilePropertyRequest = {
        applicationId: APPLICATION_ID,
        properties,
      };
      const saveUrl = `${process.env.REACT_APP_BASE_URL}${SAVE_PROPERTY}`;
      const res: SaveProfilePropertyResponse = await SendRequest.instance.post<
        SaveProfilePropertyRequest,
        SaveProfilePropertyResponse
      >(saveUrl, payload);
      if (res && res.isSuccess) {
        setIsSuccess(true);
        success('Facilities saved successfully');
        setFacilities([] as FacilityProps[]);
        dispatch(fetchHospitalProfile(REQUEST_PAYLOAD));
      } else {
        error('Facilities could not be saved');
      }
    }
  };

  const savePhotos = async (): Promise<any> => {
    let response: UploadFile = {} as UploadFile;
    let documents: DocumentDetail[] = [] as DocumentDetail[];

    if (photos !== null && photos.length > 0) {
      const formData = new FormData();
      formData.append('FileUploadType', 'Report');
      formData.append('ApplicationId', APPLICATION_ID.toString());
      const files: any = photos.flat();
      const newlyUploadedFIles = files?.filter((x: any): any => (x.uid && x.isActive))?.map((y: any): any => y?.file) || [];
      const modifiedFiles = files?.filter((x: any): any => (!x.uid && !x.isActive)) || [];
      if (newlyUploadedFIles && newlyUploadedFIles.length) {
        const url = `${process.env.REACT_APP_BASE_URL}${FILE_UPLOAD}`;
        response = await SendRequest.instance.upload(
          url,
          newlyUploadedFIles,
          formData,
        );
        if (response && response.isSuccess && response.fileUploadDetails) {
          const tempFiles = (modifiedFiles && modifiedFiles.length) ?
            [...modifiedFiles, ...response.fileUploadDetails] : [...response.fileUploadDetails];
          documents = tempFiles?.map(
            (file: FileDetail): DocumentDetail => {
              return {
                profileId: hospDetails[0].id,
                docName: file.fileName,
                docId: file.fileGuid,
                statusName: STATUS_SUBMIT,
                typeName: DOCUMENT_TYPE_INFRA_PHOTOS,
                isActive: file?.isActive === false ? false : true,
                description: '',
                approvalRemarks: '',
                id: 0,
              };
            },
          );
          submitPhotos(documents);
        }
      } else if (modifiedFiles && modifiedFiles.length) {
        documents = modifiedFiles.map(
          (file: FileDetail): DocumentDetail => {
            return {
              profileId: hospDetails[0].id,
              docName: file.fileName,
              docId: file.fileGuid,
              statusName: STATUS_SUBMIT,
              typeName: DOCUMENT_TYPE_INFRA_PHOTOS,
              isActive: file.isActive,
              description: '',
              approvalRemarks: '',
              id: 0,
            };
          },
        );
        submitPhotos(documents);
      }
    }
  };

  const submitPhotos = async (documents: DocumentDetail[]): Promise<any> => {
    const saveUrl = `${process.env.REACT_APP_BASE_URL}${SAVE_DOCUMENT}`;
    const payload: SaveDocumentRequest = {
      applicationId: APPLICATION_ID,
      documents,
    };
    const res: SaveDocumentResponse = await SendRequest.instance.post<
      SaveDocumentRequest,
      SaveDocumentResponse
    >(saveUrl, payload);
    if (res && res.isSuccess) {
      success('Photos saved successfully');
      setPhotos([] as any);
      dispatch(fetchHospitalProfile(REQUEST_PAYLOAD));
    } else {
      error('Photos could not be saved');
    }
  };

  const saveRooms = async (): Promise<any> => {
    if (rooms !== null && rooms.length > 0) {
      const roomDetails: RoomDetailsRequest[] = rooms.filter((rm: RoomDetail): any => rm.isNew).map(
        (item: RoomDetail): RoomDetailsRequest => {
          return {
            profileId: hospDetails[0].id,
            isActive: item.isActive,
            approvalRemarks: '',
            statusName: STATUS_SUBMIT,
            id: item.id,
            roomAlias: item.roomAlias,
            roomNumber: item.roomNumber,
            roomCount: item.roomCount,
            bedsCountPerRoom: item.bedsCountPerRoom,
            typeId: item.typeId || 0,
            typeName: item.typeName,
            amenities: item.amenities && item.amenities.length ? item.amenities : undefined,
            files: item.files && item.files.length ? item.files.filter((v: any): boolean => v) : undefined,
          };
        },
      );
      const payload: SaveProfilePropertyRequest = {
        applicationId: APPLICATION_ID,
        roomDetails,
      };
      const saveUrl = `${process.env.REACT_APP_BASE_URL}${SAVE_ROOMS}`;
      const res: SaveProfilePropertyResponse = await SendRequest.instance.post<
        SaveProfilePropertyRequest,
        SaveProfilePropertyResponse
      >(saveUrl, payload);
      if (res && res.isSuccess) {
        setIsSuccess(true);
        success('Rooms saved successfully');
        setRooms([]);
        dispatch(fetchHospitalProfile(REQUEST_PAYLOAD));
      } else {
        error('Rooms could not be saved');
      }
    }
  };

  const onSave = (): void => {
    saveFacilities();
    savePhotos();
    saveRooms();
  };

  const getRoomDetails = (): any => {
    const allRooms = _.first<any>(hospDetails)?.rooms;
    if (!allRooms || allRooms.length === 0) {
      return;
    } else {
      allRooms.forEach((element: any, i: number): any => {
        element.indx = i + 1;
      });
    }
    setRoomsData(allRooms);
  };

  return (
    <React.Fragment>
      { isSuccess && (
        <div style={{ display: 'none' }}>
          'Success' {JSON.stringify(photos)}
        </div>
      )}
      <Panel title={'Infrastructure Details'}>
        {!isNewEntry && (
          <>
            <Photos heading={'Photos'} photos={getPhotos} />
            <Facilities heading={'Facilities'} facilities={getFacilities} />
            <Rooms roomsDetail={roomsData} getRooms={getRooms} />
            {
              canActivate(authFeatureConstants.EDIT_HOSPITAL_PROFILE) &&
              <Button
                className="mt-md"
                onClick={onSave}
                type="primary"
                disabled={facilities?.length === 0 && photos?.length === 0 && !rooms?.length}
                style={{ float: 'right' }}>
                Save
              </Button>
            }
          </>
        )}
        {isNewEntry && <div>Please create a profile by clicking <strong>Hospital Details</strong> tab above </div>}
      </Panel>

    </React.Fragment>
  );
};

export default Infrastructure;
